import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { BehaviorSubject, Subscription, map } from 'rxjs';
import { CallType } from '../features/call/interfaces/CallType/CallType';
import { formatDate } from '../utils';
import { isNullUndefinedEmpty, momentCustomInstance as moment } from '../utils/helper';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastService } from './toast/toast.service';
import { FlecxModalService } from './flecx-modal.service';
@Injectable({
  providedIn: 'root',
})
export class LeadsService {
  //* variables
  listOfAgents: any = [];
  //* Lead Assign Form
  initialAssignFormValue = {
    fromUser: '',
    toUser: '',
  };
  leadsAssignUpdateModal: LeadAssignUpdateModal = {
    isModalOpen: false,
    userIds: [],
    userNames: [],
  };
  assignLeadForm: FormGroup = this.createLeadForm();
  isLeadListUpdated = new BehaviorSubject<{ isUpdated?: boolean }>({ isUpdated: false });
  subscriptions: Array<Subscription> = new Array<Subscription>();
  operationType: string = '';
  lisOfLeadIdsForSingleUser: Array<string> = [];

  constructor(
    private httpClient: HttpClient,
    private authService: AuthenticationService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private flecxModalService: FlecxModalService
  ) {
    this.subscriptions.push(
      this.flecxModalService.onModalAction.subscribe(({ type }) => {
        if (type === 'Transfer_Leads_Yes') {
          this.onTransferLeads();
        } else if (type === 'Transfer_Leads_No') {
          this.onRefreshData();
        }
      })
    );
  }

  /**
   * getAllOpenLeads
   * @param from  start date
   * @param to    end date
   * @param limit    data limit
   */
  getAllOpenLeads(
    from: string,
    to: string,
    campaignId: string,
    limit: Number,
    Skip: Number,
    fields: string,
    fieldValues: string,
    criteria: string
  ) {
    const url = `/lead/allOpenAssignedLeads?StartDate=${from}&EndDate=${to}&CampaignId=${campaignId}&Limit=${limit}&Skip=${Skip}&Fields=${fields}&FieldValues=${fieldValues}&Criteria=${criteria}`;
    return this.httpClient.get(url).pipe(
      map((data: any) => {
        return {
          totalCount: data.TotalCount,
          calls: data.AssignedLeadsData.map((d: any) => {
            return {
              f_name: isNullUndefinedEmpty(d.Data.FirstName),
              l_name: isNullUndefinedEmpty(d.Data.LastName),
              email: isNullUndefinedEmpty(d.Email),
              phoneNumber: isNullUndefinedEmpty(d.PhoneNumber),
              callbackNumber: isNullUndefinedEmpty(d.PhoneNumber),
              call_campaign: {
                Id: d.Campaign.Id,
                Name: d.Campaign.Name,
                Code: d.Campaign.Code,
                //! To DO ??
                LanguageCode: '',
              },
              //! To DO ??
              tfn: '',
              disposition: isNullUndefinedEmpty(d.FinalDisposition),
              countryCode: isNullUndefinedEmpty(d.CountryCode),
              skill: isNullUndefinedEmpty(d.Campaign.Name),
              campaign: isNullUndefinedEmpty(d.Campaign.Code),
              lastAssignedOn:
                d.LastAssignedOn === '0001-01-01T00:00:00Z'
                  ? 'N/A'
                  : isNullUndefinedEmpty(moment(d.LastAssignedOn).format('DD-MMM-YY')),
              lastDialedOn:
                d.LastDialedOn === '0001-01-01T00:00:00Z'
                  ? 'N/A'
                  : isNullUndefinedEmpty(moment(d.LastDialedOn).format('DD-MMM-YY')),
              nextDialedOn: d.DialDate ? moment(d.DialDate).format('DD-MMM-YY') : 'N/A',
              nextFlowId: d.NextFlowId ? d.NextFlowId : 'N/A',
              dialCount: d.DialCount,
              payload: JSON.parse(d.Data.Payload),
              lead_source: d.LeadSource,
              id: d.Id,
              rowDetail: {
                timeZone: 'America/Los_Angeles',
                meetingSource: 'Phone',
                meetingMinutes: 30,
                meetingStartDate: moment().tz('America/Los_Angeles', true),
                meetingEndDate: moment().tz('America/Los_Angeles', true).add(30, 'minutes'),
                enabled: false,
              },
            };
          }),
        };
      })
    );
  }

  /**
   * updateLeadMeeting
   * @param meetingStartDate
   * @param meetingEndDate
   * @param meetingSource
   */
  updateLeadMeeting(data: any) {
    const url = `/lead/updateLeadMeetingScheduled`;
    return this.httpClient.put(url, data);
  }

  getOpenAssignedLeadsList(payload: any) {
    const url = `/lead/getOpenAssignedLeads`;
    return this.httpClient.post(url, payload);
  }

  createLeadForm() {
    return this.fb.group({
      fromUser: ['', [Validators.required]],
      toUser: ['', [Validators.required]],
    });
  }

  getAssignLeadsFormValue(controlName: string) {
    return this.assignLeadForm.controls[controlName].value;
  }

  clearUserForm(emitEvent: boolean = false) {
    this.assignLeadForm.reset(this.initialAssignFormValue, { emitEvent: emitEvent });
  }

  closeLeadsAssignUpdateModal() {
    this.leadsAssignUpdateModal = {
      isModalOpen: false,
      userIds: [],
      userNames: [],
    };
    //* Clean up work
    this.clearUserForm();
    this.operationType = '';
    this.lisOfLeadIdsForSingleUser = [];
  }

  checkAssignLeadFormFieldValidation(fieldName: string) {
    return (
      this.assignLeadForm.get(fieldName)?.invalid &&
      (this.assignLeadForm.get(fieldName)?.dirty || this.assignLeadForm.get(fieldName)?.touched)
    );
  }
  isFormValid(form: FormGroup): boolean {
    //* The validation status of the control. There are four possible validation status values:

    // VALID: This control has passed all validation checks.
    // INVALID: This control has failed at least one validation check.
    // PENDING: This control is in the midst of conducting a validation check.
    // DISABLED: This control is exempt from validation checks.

    return form.disabled ? true : form.valid;
  }

  getAllAgents() {
    const url = `/user/getUsers`;
    const data = {
      OnlineUser: false,
      OnlySup: false,
    };
    return this.httpClient.post(url, data);
  }

  toggleAssignLeadModal() {
    this.leadsAssignUpdateModal.isModalOpen = !this.leadsAssignUpdateModal.isModalOpen;
  }

  setLeadsAssignment(payload: any) {
    const url = `/lead/transferLeads`;
    return this.httpClient.post(url, payload);
  }

  handleTransferLeads(type: string, selectedUsers: any) {
    this.operationType = type;
    this.assignLeadForm.markAllAsTouched();
    if (this.getAssignLeadsFormValue('fromUser') === this.getAssignLeadsFormValue('toUser')) {
      this.toastService.error('Leads of the same user can not be transferred');
      return;
    }
    if (this.isFormValid(this.assignLeadForm)) {
      //Modal Confirmation
      this.flecxModalService.isOpen = true;
      this.flecxModalService.isDoubbleButton = true;
      this.flecxModalService.fButtonType = 'Transfer_Leads_Yes';
      this.flecxModalService.sButtonType = 'Transfer_Leads_No';
      this.flecxModalService.fButtonText = 'Yes';
      this.flecxModalService.sButtonText = 'No';
      this.flecxModalService.text = `<p>Are you sure you want to ${type} leads from <strong>${selectedUsers.fromUserName}</strong> to <strong>${selectedUsers.toUserName}</strong></p>`;
    }
  }

  handleTransferLeadsToSingleUser(type: string, listOfLeadIds: Array<string>, targetTransferAgent: any) {
    this.operationType = type;
    this.lisOfLeadIdsForSingleUser = listOfLeadIds;
    this.assignLeadForm.controls['toUser'].setValue(targetTransferAgent?.Id);
    //Modal Confirmation
    this.flecxModalService.isOpen = true;
    this.flecxModalService.isDoubbleButton = true;
    this.flecxModalService.fButtonType = 'Transfer_Leads_Yes';
    this.flecxModalService.sButtonType = 'Transfer_Leads_No';
    this.flecxModalService.fButtonText = 'Yes';
    this.flecxModalService.sButtonText = 'No';
    this.flecxModalService.text = `<p>Are you sure you want to transfer <strong> ${listOfLeadIds.length}</strong> leads to <strong>${targetTransferAgent?.UserName}</strong></p>`;
  }

  onTransferLeads() {
    let payload = {
      OperationType: this.operationType,
      ListOfLeadIds: this.lisOfLeadIdsForSingleUser,
      FromUser: this.getAssignLeadsFormValue('fromUser'),
      ToUser: this.getAssignLeadsFormValue('toUser'),
    };
    this.setLeadsAssignment(payload).subscribe({
      next: (data: any) => {
        // Handle next value
        this.toastService.success('Leads Transferred Successfully');
        this.onRefreshData();
      },
      error: (error: any) => {
        // Handle error
        this.toastService.error('Error Occurred');
      },
      complete: () => {
        // Handle complete
        // No errors, route to new page here
      },
    });
  }

  onRefreshData() {
    this.toggleAssignLeadModal();
    this.closeLeadsAssignUpdateModal();
    this.isLeadListUpdated.next({ isUpdated: true });
  }
}

export interface LeadAssignUpdateModal {
  isModalOpen: boolean;
  userIds: Array<string>;
  userNames: Array<string>;
}
